import React from 'react';
import PropTypes from 'prop-types';
import { LuInfo, LuSearch } from 'react-icons/lu';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import useSmoText from '../smoText';
import { ChartCard, useChartName, useChartTheme } from './chartKit';
import { CLICKABLE, TEXT_TONES, hexTone } from './tones';

/** Label / value table whose rows open the issue details when above zero. */
const KpiTable = ({ title, rows, onOpen }) => {
  const { label } = useSmoText();
  return (
    <ChartCard title={title}>
      <ul className="tw-divide-y tw-divide-slate-200/70 dark:tw-divide-white/[0.06]">
        {rows.map((row) => {
          const clickable = row.count > 0 && !!row.metric;
          const content = (
            <>
              <span className="tw-min-w-0 tw-flex-1 tw-text-left tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{row.label}</span>
              <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-tabular-nums ${hexTone(row.color)}`}>
                {row.text}
                {clickable && <LuSearch aria-hidden="true" className="tw-h-3 tw-w-3 tw-opacity-60" />}
              </span>
            </>
          );
          return (
            <li key={row.label}>
              {clickable ? (
                <button type="button" onClick={() => onOpen(row)} title={label('common.detailHint')} className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-lg tw-bg-transparent tw-px-2 tw-py-2.5 hover:tw-bg-slate-900/[0.03] dark:hover:tw-bg-white/[0.04]">
                  {content}
                </button>
              ) : (
                <div className="tw-flex tw-items-center tw-gap-3 tw-px-2 tw-py-2.5">{content}</div>
              )}
            </li>
          );
        })}
      </ul>
    </ChartCard>
  );
};

KpiTable.propTypes = { title: PropTypes.string.isRequired, rows: PropTypes.array.isRequired, onOpen: PropTypes.func.isRequired };

/** Body of "Kalite & Klinik Risk Paneli" (Section 2). */
const QualityRiskPanel = ({ summary, visits, capaDashboard, openIssueModal }) => {
  const { label } = useSmoText();
  const chart = useChartTheme();
  const chartName = useChartName();
  const inVisits = (name) => label('quality.inVisits', { name, count: summary.total });
  const pctOf = (n, d) => (d > 0 ? Math.round(n / d * 100) : 0);

  const safetyRows = [
    { label: label('quality.aeSaeReported'), metric: 'aesae', count: summary.aeSaeCount, color: summary.aeSaeCount > 0 ? '#9b2c2c' : '#276749', text: `${summary.aeSaeCount}` },
    { label: label('quality.dcfCreated'), metric: 'dcf', count: summary.dcfCount, color: undefined, text: `${summary.dcfCount}` },
    { label: label('metrics.deviation'), metric: 'deviation', count: summary.deviationCount, color: summary.deviationCount > 0 ? '#c53030' : undefined, text: `${summary.deviationCount} (%${pctOf(summary.deviationCount, summary.total)})` },
    { label: label('metrics.capa'), metric: 'capa', count: summary.capaCount, color: summary.capaCount > 0 ? '#c05621' : undefined, text: `${summary.capaCount}` },
    { label: label('quality.missedVisit'), metric: 'missed', count: summary.missedCount, color: summary.missedCount > 0 ? '#c53030' : undefined, text: `${summary.missedCount}` },
  ];
  const siteRows = [
    { label: label('quality.totalSite'), count: 0, text: `${summary.siteCount}` },
    { label: label('quality.screenFail'), metric: 'screenFail', count: summary.screenFail, color: summary.screenFail > 0 ? '#c05621' : undefined, text: `${summary.screenFail} ${summary.siteCount > 0 ? `(%${pctOf(summary.screenFail, summary.siteCount)})` : ''}` },
    { label: label('quality.dropPatient'), metric: 'dropPatient', count: summary.dropPatient, color: summary.dropPatient > 0 ? '#c05621' : undefined, text: `${summary.dropPatient} ${summary.siteCount > 0 ? `(%${pctOf(summary.dropPatient, summary.siteCount)})` : ''}` },
    { label: label('quality.slaOverdue'), metric: 'overdue', count: summary.overdueCount, color: summary.overdueCount > 0 ? '#c53030' : '#276749', text: `${summary.overdueCount}` },
    { label: label('quality.missingProcessEnd'), metric: 'missingProcessEnd', count: summary.missingProcessEnd, color: summary.missingProcessEnd > 0 ? '#c05621' : undefined, text: `${summary.missingProcessEnd}` },
  ];

  return (
    <div className="tw-space-y-4">
      <p className="tw-flex tw-items-start tw-gap-1.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
        <LuInfo aria-hidden="true" className="tw-mt-px tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-sky-500" />
        {label('quality.clickHint')}
      </p>

      {/* ── Quality Metric Tiles (clickable when count > 0) ── */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3 xl:tw-grid-cols-6">
        {summary.qualityTiles.map((tile) => {
          const clickable = tile.count > 0;
          const name = label(`metrics.${tile.metric}`);
          const content = (
            <>
              <span className={`tw-block tw-text-2xl tw-font-bold tw-tabular-nums ${hexTone(tile.color)}`}>{tile.count}</span>
              <span className="tw-mt-1 tw-block tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{name}</span>
              {tile.rate !== null && <span className="tw-mt-0.5 tw-block tw-text-[11px] tw-text-slate-500 dark:tw-text-slate-400">%{tile.rate}</span>}
            </>
          );
          const base = 'tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3.5 tw-text-center dark:tw-border-white/10 dark:tw-bg-white/[0.03]';
          return clickable ? (
            <button key={tile.metric} type="button" onClick={() => openIssueModal(inVisits(name), visits, tile.metric)} title={label('common.detailHint')} className={`${base} ${CLICKABLE}`}>{content}</button>
          ) : (
            <div key={tile.metric} className={base}>{content}</div>
          );
        })}
      </div>

      {/* ── Risk Summary Table ── */}
      <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
        <KpiTable title={label('quality.safetyTitle')} rows={safetyRows} onOpen={(row) => openIssueModal(inVisits(row.label), visits, row.metric)} />
        <KpiTable title={label('quality.siteTitle')} rows={siteRows} onOpen={(row) => openIssueModal(inVisits(row.label), visits, row.metric)} />
      </div>

      {/* ── Issue Breakdown Charts ── */}
      {capaDashboard && (capaDashboard.barChartData.length > 0 || capaDashboard.trendData.length > 1) && (
        <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
          {capaDashboard.barChartData.length > 0 && (
            <ChartCard title={label('quality.coordinatorChart')}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={capaDashboard.barChartData} margin={{ bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="name" tick={chart.tick} angle={-20} textAnchor="end" />
                  <YAxis allowDecimals={false} tick={chart.tick} />
                  <Tooltip labelFormatter={(name) => { const item = capaDashboard.barChartData.find((d) => d.name === name); return item?.fullName || name; }} {...chart.tooltip} />
                  <Legend wrapperStyle={chart.legend} />
                  <Bar dataKey="Sorun" name={chartName('Sorun')} fill="#f6ad55" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Kaçırılan" name={chartName('Kaçırılan')} fill="#fc8181" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
          {capaDashboard.trendData.length > 1 && (
            <ChartCard title={label('quality.monthlyIssues')}>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={capaDashboard.trendData} margin={{ bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="month" tick={chart.tick} />
                  <YAxis allowDecimals={false} tick={chart.tick} />
                  <Tooltip {...chart.tooltip} />
                  <Legend wrapperStyle={chart.legend} />
                  <Line type="monotone" dataKey="Sorun" name={chartName('Sorun')} stroke="#f6ad55" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="Kaçırılan" name={chartName('Kaçırılan')} stroke="#fc8181" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}
      {capaDashboard && (capaDashboard.barChartData.length > 0 || capaDashboard.trendData.length > 1) && (
        <p className={`tw-text-xs ${TEXT_TONES.muted} tw-opacity-70`}>{label('quality.chartsNote')}</p>
      )}
    </div>
  );
};

QualityRiskPanel.propTypes = {
  summary: PropTypes.object.isRequired,
  visits: PropTypes.array.isRequired,
  capaDashboard: PropTypes.object,
  openIssueModal: PropTypes.func.isRequired,
};

export default QualityRiskPanel;
